import React, {Fragment} from 'react';
import withRedux from 'next-redux-wrapper';
import {NextIntlProvider} from 'next-intl';
import {ThemeProvider as MaterialThemeProvider, createMuiTheme} from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import {Provider} from 'react-redux';
import App from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import dynamic from 'next/dynamic';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import createStore from '../src/store/createStore';
import theme from '../src/theme';
import config from '../src/config';
import {getOrNull, isWhite} from '../src/libs/utils';
import getTextContrastColor from '../src/libs/textContrastColor';
import * as ga from '../src/libs/ga';
import '@datadog/browser-rum/bundle/datadog-rum';

const VersionUpgrade = dynamic(() => import('../src/components/VersionUpgrade'), {
  ssr: false,
});

const userBackJS = `window.Userback = window.Userback || {};
Userback.access_token = '11202|${config.isProd ? '23305' : '70674'}|gBAPqCkFMVNBNrJYzp9aqPziq';
(function(d) {
    var s = d.createElement('script');s.async = true;
    s.src = 'https://static.userback.io/widget/v1.js';
    (d.head || d.body).appendChild(s);
})(document);`;

// use Google Analytics to track page views
Router.events.on("routeChangeComplete", ga.pageview);

class MyApp extends App {

    componentDidMount() {
        const {pageProps} = this.props;
        const reservationId = getOrNull('reservation.id', pageProps);

        // Remove the server-side injected CSS
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles && jssStyles.parentNode) {
            jssStyles.parentNode.removeChild(jssStyles);
        }

        const context = reservationId ? {
            id: pageProps.reservation.id,
            customer: pageProps.reservation.host_name,
            user_id: pageProps.reservation.hid,
            listing_id: pageProps.reservation.lid,
            source: pageProps.reservation.source,
            status: pageProps.reservation.status,
            risk_color: pageProps.reservation.protect_color,
            whitelisted: pageProps.reservation.whitelisted,
            confirmation_code: pageProps.reservation.confirmation_code,
            without_listing: pageProps.reservation.without_listing,
        } : {};

        // init DataDog RUM client
        const datadog = typeof window !== 'undefined' && window.DD_RUM;
        if (datadog) {
            datadog.init({
                clientToken: config.DATADOG_RUM_CLIENT_TOKEN,
                applicationId: config.DATADOG_RUM_APP_ID,
                sampleRate: 100,
                trackInteractions: true,
                silentMultipleInit: true,
                env: config.stage,
                service: 'guestportal',
                version: config.VERSION,
            });
            if (reservationId) {
                datadog.addRumGlobalContext('reservation', context);
            }
        }

        const Userback = typeof window !== 'undefined' && window.Userback;
        if (Userback) {
            try {
                Userback.custom_data = context;
                Userback.email = getOrNull('reservation.form.email', pageProps) || getOrNull('reservation.guest_email', pageProps);
                Userback.name = getOrNull('reservation.form.full_name', pageProps) || getOrNull('reservation.guest_full_name', pageProps);
            } catch (error) {
                console.error(`Failed to load bug reporting plugin: ${error}`);
            }
        }
    }

    componentDidCatch(error, errorInfo) {
        const {pageProps} = this.props;
        const {reservationId} = pageProps || {};
        console.error(`Exception caught in Guest Portal top-most component for reservation ${reservationId}: ${error}`);
        super.componentDidCatch(error, errorInfo);
    }

    render() {
        const {Component, store, router, pageProps, messages} = this.props;
        const {locale} = router;
        let userTheme = theme;
        const primaryColor = getOrNull('reservation.company.color_primary', pageProps);
        if (primaryColor && !isWhite(primaryColor)) {
            userTheme = createMuiTheme({
                ...theme,
                palette: {
                    ...theme.palette,
                    primary: {
                        main: primaryColor,
                        contrastText: getTextContrastColor(primaryColor)
                    },
                    secondary: {
                        main: primaryColor,
                    }
                }
            });
        }

        let header = (
            <Head>
                <title>Guest Portal</title>


                {/* Use minimum-scale=1 to enable GPU rasterization */}
                <meta
                    name="viewport"
                    content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
                />

                {/* ask robots not to index this site */}
                <meta name="googlebot" content="noindex"/>
                <meta name="robots" content="noindex"/>
            </Head>
        );

        if (pageProps && pageProps.reservation && pageProps.reservation.id) {
            const reservation = pageProps.reservation;
            let siteDomain = reservation.settings && reservation.settings.guest_portal_custom_domain;
            if (!siteDomain || siteDomain === '') {
                siteDomain = 'verifyhub.org';
            }
            header = (
                <Head>
                    <title>Check-in for {reservation.guest_first_name}</title>
                    <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width"/>
                    <meta property="og:title" content={`Check-in for ${reservation.guest_first_name}`}/>
                    <meta name="description"
                          content={`Hi ${reservation.guest_first_name}, please complete the following steps to confirm your upcoming reservation in ${reservation.listing_city}, ${reservation.listing_country}.`}/>
                    <meta property="og:description"
                          content={`Hi ${reservation.guest_first_name}, please complete the following steps to confirm your upcoming reservation in ${reservation.listing_city}, ${reservation.listing_country}.`}/>
                    <meta property="og:image"
                          content={`https:${reservation.listing_picture_thumbnail.replace(/^https?:/, '')}`}/>
                    <meta property="og:image:secure_url"
                          content={`https:${reservation.listing_picture_thumbnail.replace(/^https?:/, '')}`}/>
                    <meta property="og:image:alt" content={reservation.listing_name}/>
                    <meta property="og:url" content={`https://${siteDomain}/${reservation.id}`}/>
                    <meta name="googlebot" content="noindex"/>
                    <meta name="robots" content="noindex"/>

                    {/* User feedback tool */}
                    <script dangerouslySetInnerHTML={{__html: userBackJS}}/>
                </Head>
            );
        }

        return (
            <Fragment>
                {header}
                <MaterialThemeProvider theme={userTheme}>
                    <CssBaseline/>
                    <Provider store={store}>
                        <NextIntlProvider
                            messages={{...messages, ...pageProps.messages}}
                            locale={locale || 'en'}
                            timeZone={getOrNull('reservation.guest_timezone', pageProps) || 'America/Toronto'}
                        >
                            <MuiPickersUtilsProvider utils={MomentUtils} locale={locale || 'en'}>
                                <Component router={router} {...pageProps}/>
                            </MuiPickersUtilsProvider>
                            <VersionUpgrade/>
                        </NextIntlProvider>
                    </Provider>
                </MaterialThemeProvider>
            </Fragment>
        );
    }
}

MyApp.getInitialProps = async (context = {}) => {
    const {locale, locales, defaultLocale} = context.router;
    let messages;
    try {
        messages = require(`../locales/${locale}.json`);
    } catch (e) {
        console.error(`Failed to load requested locale ${locale} (${locales}), falling back to ${defaultLocale}`);
        try {
            messages = require(`../locales/en.json`);
        } catch (e2) {
            console.error(`Failed to load fallback translations`);
            messages = {};
        }
    }
    return {
        ...(await App.getInitialProps(context)),
        messages,
    };
};

export default withRedux(createStore)(MyApp);
